export type GoogleEventParameters = Record<string, string | number | boolean | undefined>;

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

let initialized = false;
const seenEvents = new Set<string>();

const getAnalyticsId = () => process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID?.trim();
const getAdsId = () => process.env.NEXT_PUBLIC_GOOGLE_ADS_ID?.trim();
const getPurchaseLabel = () => process.env.NEXT_PUBLIC_GOOGLE_ADS_PURCHASE_LABEL?.trim();

const getGtag = () => {
  if (typeof window === "undefined") return undefined;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || ((...args: unknown[]) => window.dataLayer?.push(args));
  return window.gtag;
};

const storageKey = (key: string) => `sr_google_event:${key}`;

export const googleAnalytics = {
  isConfigured: () => typeof window !== "undefined" && Boolean(getAnalyticsId() || getAdsId()),

  hasPurchaseConversion: () => Boolean(getAdsId() && getPurchaseLabel()),

  init: () => {
    const gtag = getGtag();
    if (!gtag || initialized) return;
    initialized = true;
    gtag("js", new Date());
    const analyticsId = getAnalyticsId();
    const adsId = getAdsId();
    if (analyticsId) gtag("config", analyticsId, { send_page_view: false });
    if (adsId && adsId !== analyticsId) gtag("config", adsId, { send_page_view: false });
  },

  pageView: (pathname: string) => {
    if (!initialized) return;
    getGtag()?.("event", "page_view", {
      page_path: pathname,
      page_location: window.location.href,
      page_title: document.title,
    });
  },

  purchase: ({
    bookingUid,
    eventTypeId,
    eventTypeTitle,
    value,
    currency,
  }: {
    bookingUid: string;
    eventTypeId: number;
    eventTypeTitle: string;
    value: number;
    currency: string;
  }) => {
    if (!initialized) return;
    const parameters = {
      transaction_id: bookingUid,
      value,
      currency: currency.toUpperCase(),
      items: [{ item_id: String(eventTypeId), item_name: eventTypeTitle, quantity: 1, price: value }],
    };
    getGtag()?.("event", "purchase", parameters);

    const adsId = getAdsId();
    const purchaseLabel = getPurchaseLabel();
    if (adsId && purchaseLabel) {
      getGtag()?.("event", "conversion", {
        send_to: `${adsId}/${purchaseLabel}`,
        value,
        currency: currency.toUpperCase(),
        transaction_id: bookingUid,
      });
    }
  },

  oncePersistent: (key: string, callback: () => void) => {
    if (seenEvents.has(key)) return;
    try {
      if (localStorage.getItem(storageKey(key))) {
        seenEvents.add(key);
        return;
      }
      localStorage.setItem(storageKey(key), "1");
    } catch {
      try {
        if (sessionStorage.getItem(storageKey(key))) {
          seenEvents.add(key);
          return;
        }
        sessionStorage.setItem(storageKey(key), "1");
      } catch {
        // Analytics must never block booking when storage is unavailable.
      }
    }
    seenEvents.add(key);
    callback();
  },
};
